import { SupportService as ClientSupportService } from '@acme/api';
import type { TicketResponse } from '@acme/api/admin';
import { SupportService as AdminSupportService } from '@acme/api/admin';
import {
  PaginationParams,
  SendMessageRequest,
  SupportChatProps,
  TicketCard,
} from '@acme/domains';
import { ChatWindow } from '@acme/features';
import { downloadFile } from '@acme/foundation';
import { Spinner } from 'flowbite-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<TicketResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTicket = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await AdminSupportService.getApiAdminV1SupportTicket({
        id,
      });
      if (response.data) {
        setTicket(response.data);
      }
    } catch (err) {
      console.error('Ошибка загрузки обращения:', err);
      setError('Ошибка загрузки обращения. Попробуйте обновить страницу.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  const handleBack = () => {
    navigate('/support/tickets');
  };

  const handleDownloadAttachment = async (
    attachmentId: string,
    fileName: string
  ) => {
    try {
      const blob = await ClientSupportService.getApiV1SupportMessageAttachment({
        id: attachmentId,
      });
      downloadFile({ data: blob, fileName });
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-lg text-gray-500">
          {error || 'Обращение не найдено'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2.5 text-sm">
        <button
          onClick={handleBack}
          className="font-medium text-gray-700 hover:text-gray-900 transition-colors"
        >
          Обращения
        </button>
        <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 6 10">
          <path
            d="M1 1L5 5L1 9"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="font-medium text-gray-500">#{ticket.number}</span>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <TicketCard
          ticket={ticket}
          onDownloadAttachment={handleDownloadAttachment}
          dateField="createdAt"
        />

        <div className="-mx-6 pb-6 px-6 border-b border-b-gray-200 mb-6 mt-6"></div>
        <ChatWindow
          messageListVariant="ticket"
          ticketId={id}
          api={{
            companionLabelText: ticket.userName || 'Собеседник',
            loadChatHistory: (() => {
              const loadChatHistory: SupportChatProps['loadChatHistory'] = (
                params: PaginationParams
              ) => {
                if (!id) {
                  throw new Error('Ticket ID is required');
                }
                return AdminSupportService.getApiAdminV1SupportTicketMessage({
                  ticketId: id,
                  pageNumber: params.pageNumber,
                  pageSize: params.pageSize,
                });
              };
              return loadChatHistory;
            })(),
            sendMessage: (req: SendMessageRequest) => {
              if (!id) {
                throw new Error('Ticket ID is required');
              }
              return AdminSupportService.postApiAdminV1SupportTicketMessageSend(
                {
                  formData: {
                    ticketId: id,
                    text: req.formData.text,
                    files: req.formData.files,
                  },
                }
              );
            },
            getAttachmentReq: ({ id: attachmentId }: { id: string }) => {
              return ClientSupportService.getApiV1SupportMessageAttachment({
                id: attachmentId,
              });
            },
          }}
        />
      </div>
    </div>
  );
}
